import {
  deriveActualTimes,
  deriveClientAddress,
  deriveServiceTypeDescription,
  deriveServiceTypeSelectorParams,
} from '~appointments/lib/derive';
import { initialiseUserOption } from '~appointments/lib/initialise';
import { onChangeClient, onChangePackageHandler } from '~appointments/lib/onchange';
import { showLocations } from '~appointments/lib/show';
import { validateStartEndTimes } from '~appointments/lib/validate';

import { FieldProps } from '~components/form-fields';
import FormInputActualTimes from '~appointments/components/FormInputActualTimes';
import FormInputCareWorkers from '~appointments/components/FormInputCareWorkers';
import FormInputDate from '~appointments/components/FormInputDate';
import FormInputPackageSelector from '~appointments/components/FormInputPackageSelector';
import FormInputStartEndTimes from '~appointments/components/FormInputStartEndTimes';
import FormInputWorkerAppointments from '~appointments/components/FormInputWorkerAppointments';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getClients } from '~main/actions/clients';

import { selectLocations } from '~appointments/selectors/locations';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectAppointmentStatuses } from '~appointments/selectors/statuses';
import { selectLookupCareWorkers } from '~care-workers/selectors/users';
import { selectLookupClients } from '~main/selectors/clients/clients';

const showWorkerAppointments = ({ user_id, date }: { user_id?: string; date?: string }) =>
  !isNaN(parseInt(user_id ?? '')) && (date ?? '').length > 0;

const caseManagementFields: FieldProps = {
  id: {
    type: 'hidden',
  },
  notes: {
    type: 'hidden',
  },
  date: {
    type: 'custom',
    component: FormInputDate,
    caption: 'Appointment Date',
    required: true,
    colSize: 4,
    order: 100,
  },
  client_name: {
    type: 'select',
    field: 'client_id',
    caption: 'Client / Facility',
    deriveDescription: deriveClientAddress,
    onChange: onChangeClient,
    className: 'text-left',
    required: true,
    colSize: 4,
    order: 200,
    selectorOptions: selectLookupClients,
    selectorActions: getClients,
    manualReload: true,
  },
  package_fixed_id: {
    type: 'custom',
    component: FormInputPackageSelector,
    dependencies: ['package_fixed_id', 'client_id', 'date'],
    onChange: onChangePackageHandler,
    required: true,
    colSize: 6,
    order: 300,
  },
  location_override_id: {
    type: 'select',
    caption: 'Location',
    colSize: 4,
    description: 'Optional, defaults to client home address',
    order: 400,
    show: showLocations,
    selectorOptions: selectLocations,
    dataCy: 'location-id-select',
  },
  service_type_id: {
    type: 'select-lazy',
    caption: 'Service Type',
    selectorOptions: selectAppointmentServiceTypes,
    selectorActions: getServiceTypes,
    selectorParams: deriveServiceTypeSelectorParams,
    deriveWarning: deriveServiceTypeDescription,
    required: true,
    order: 500,
    colSize: 4,
    dataCy: 'service-type-id-select',
  },
  user_id: {
    type: 'custom',
    caption: 'Care Partner',
    component: FormInputCareWorkers,
    selectorOptions: selectLookupCareWorkers,
    dependencies: ['client_id', 'user_id', 'date', 'id', 'repeat', 'support_coordination', 'case_management'],
    initialiseInitialValue: initialiseUserOption,
    colSize: 6,
    order: 600,
  },
  start_end_time: {
    type: 'custom',
    caption: 'Start/End Times',
    colSize: 6,
    component: FormInputStartEndTimes,
    dependencies: ['date', 'start_time', 'end_time', 'ends_next_day'],
    validate: validateStartEndTimes,
    order: 700,
  },
  status_id: {
    type: 'readonly',
    caption: 'Marked as',
    description: 'Status will be set on creation',
    dataCy: 'status_id',
    selectorOptions: selectAppointmentStatuses,
    order: 900,
  },
  user_appointments: {
    type: 'custom',
    component: FormInputWorkerAppointments,
    dependencies: ['date', 'user_id', 'case_management'],
    show: showWorkerAppointments,
    colSize: 6,
    order: 950,
  },
};

const caseManagementEditFields: FieldProps = {
  ...caseManagementFields,
  actual_times: {
    type: 'custom',
    caption: 'Actual Times',
    labelCaption: 'Used for claim calculations',
    component: FormInputActualTimes,
    deriveValue: deriveActualTimes,
    colSize: 6,
    order: 800,
  },
};

export { caseManagementFields, caseManagementEditFields };
